'use client';

/**
 * Featured-video spotlight card — 2/1 split with a 16:9 preview on
 * the left and metadata + action row on the right. Ported from
 * ThemeMaker's {@code blocks/hero/HeroSpotlight}.
 *
 * <h3>Visual recipe</h3>
 * <ul>
 *   <li>Optional {@code sectionLabel} eyebrow above the card</li>
 *   <li>Video preview with NEW chip + duration badge overlays</li>
 *   <li>Right pane: kicker pill → creator avatar + title → meta line
 *       (views · ago) → description → Like / Later / List / Share
 *       action row (decorative)</li>
 * </ul>
 *
 * <p>The action buttons are intentionally decorative — they're a
 * marketing-layout cue. Real wishlist / progress wiring lives in
 * {@code @akademi/site-builder-runtime} islands.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Heart, Bookmark, List, Share2 } from 'lucide-react';
import { Button, Card } from '@akademi/ui';
import { BlockRoot } from '../_shared/BlockRoot';
import { cardReset } from '../_shared/styles';

type Localized = string | Record<string, string>;

export interface HeroSpotlightProps {
  kicker?: string;
  title?: string;
  description?: string;
  ctaLabel?: string;
  ctaHref?: string;
  creator?: string;
  duration?: string;
  views?: string;
  ago?: string;
  /** Eyebrow above the card (e.g. "Spotlight"). When set, the
   *  card gets a preamble row. */
  sectionLabel?: string;
  /** Supplementary line under {@link sectionLabel} (e.g. "Editor's
   *  pick of the week"). */
  sectionSubtitle?: string;
  /** Show the Like / Later / List / Share row. Default true. */
  showActions?: boolean;
  /** Show the NEW chip on the video preview. */
  isNew?: boolean;
  /** Optional background image; falls back to a gradient. */
  imageUrl?: string;
  /** 'full' (default) lets content span the section edge-to-edge;
   *  'theme' constrains it to the page's max-width container. */
  maxWidth?: 'full' | 'theme';
}

export function HeroSpotlight({
  kicker,
  title,
  description,
  ctaLabel,
  ctaHref = '/lxp/catalog',
  creator,
  duration = '08:42',
  views,
  ago,
  sectionLabel,
  sectionSubtitle,
  showActions = true,
  isNew = true,
  imageUrl,
  maxWidth = 'full',
}: HeroSpotlightProps) {
  const { t } = useTranslation('site-builder-blocks');
  const kickerText = kicker ?? t('hero.spotlight.kicker');
  const titleText = title ?? t('hero.spotlight.title');
  const descriptionText = description ?? t('hero.spotlight.description');
  const ctaText = ctaLabel ?? t('hero.spotlight.cta');
  const creatorText = creator ?? t('hero.spotlight.creator');
  const viewsText = views ?? t('hero.spotlight.views');
  const agoText = ago ?? t('hero.spotlight.ago');
  return (
    <BlockRoot as="section" style={{ padding: 'var(--space-8, 32px) 0' }}>
      <div
        style={{
          maxWidth:
            maxWidth === 'theme'
              ? 'var(--sb-max-width-1200, var(--app-max-width, 1200px))'
              : 'none',
          margin: '0 auto',
          padding: maxWidth === 'full' ? '0 var(--space-6, 24px)' : 0,
        }}
      >
        {/* Section preamble — only when sectionLabel is set. */}
        {sectionLabel && (
          <div style={{ marginBottom: 'var(--space-4, 16px)' }}>
            <h2
              style={{
                margin: 0,
                fontSize: 'var(--font-size-lg, 18px)',
                fontWeight: 'var(--font-weight-extrabold, 800)',
                color: 'var(--color-text-primary, #18181b)',
                letterSpacing: 'var(--font-tracking-snug, -0.01em)',
              }}
            >
              {sectionLabel}
            </h2>
            {sectionSubtitle && (
              <p
                style={{
                  margin: 'var(--space-0-5, 2px) 0 0',
                  fontSize: 'var(--font-size-xs, 12px)',
                  color: 'var(--color-text-tertiary, #6a6f73)',
                }}
              >
                {sectionSubtitle}
              </p>
            )}
          </div>
        )}

        {/* Card chrome wraps both panes — one cohesive panel rather
         *  than two visually-separate columns. */}
        <Card
          className="hero-spotlight-grid"
          style={{
            ...cardReset,
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
            gap: 0,
            alignItems: 'stretch',
            background: 'var(--color-surface-card, #fff)',
            borderRadius: 'var(--radius-lg, 16px)',
            overflow: 'hidden',
          }}
        >
          {/* Left pane — video preview */}
          <div
            style={{
              position: 'relative',
              width: '100%',
              minWidth: 0,
              aspectRatio: '16 / 9',
              background: imageUrl
                ? `center / cover no-repeat url("${imageUrl}")`
                : `linear-gradient(135deg, var(--color-brand-500, #3b5bdb), var(--color-text-primary, #18181b))`,
            }}
          >
            {/* Subtle dark overlay so overlay chips stay legible. */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: imageUrl ? 'rgba(0,0,0,0.15)' : 'transparent',
              }}
            />
            {isNew && (
              <span
                style={{
                  position: 'absolute',
                  top: 'var(--space-4, 16px)',
                  left: 'var(--space-4, 16px)',
                  background: 'var(--color-brand-500, #3b5bdb)',
                  color: 'var(--color-text-on-brand, #fff)',
                  padding: 'var(--space-1, 4px) var(--space-2-5, 10px)',
                  borderRadius: 'var(--radius-pill, 9999px)',
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: 'var(--font-tracking-wider, 0.06em)',
                  textTransform: 'uppercase',
                }}
              >
                NEW
              </span>
            )}
            <div
              style={{
                position: 'absolute',
                bottom: 'var(--space-3, 12px)',
                right: 'var(--space-3, 12px)',
                background: 'rgba(0,0,0,0.7)',
                color: 'var(--color-text-on-brand, #fff)',
                padding: 'var(--space-1, 4px) var(--space-2-5, 10px)',
                borderRadius: 'var(--radius-sm, 4px)',
                fontSize: 'var(--font-size-xs, 12px)',
                fontFamily: 'var(--font-family-mono, monospace)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {duration}
            </div>
          </div>

          {/* Right pane — metadata */}
          <div
            style={{
              padding: 'var(--space-6, 24px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Featured pill — solid brand bg + white text, bright tag. */}
            <span
              style={{
                display: 'inline-block',
                alignSelf: 'flex-start',
                padding: 'var(--space-1, 4px) var(--space-2-5, 10px)',
                background: 'var(--color-brand-500, #3b5bdb)',
                color: 'var(--color-text-on-brand, #fff)',
                borderRadius: 'var(--radius-pill, 9999px)',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: 'var(--font-tracking-wider, 0.06em)',
                marginBottom: 'var(--space-3, 12px)',
              }}
            >
              {kickerText}
            </span>

            {/* Title. */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2-5, 10px)',
                marginBottom: 'var(--space-2, 8px)',
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-xl, 22px)',
                  fontFamily: 'var(--font-family-display, inherit)',
                  fontWeight: 'var(--font-weight-extrabold, 800)',
                  lineHeight: 'var(--line-height-tight, 1.2)',
                  color: 'var(--color-text-primary, #18181b)',
                  letterSpacing: 'var(--font-tracking-snug, -0.01em)',
                }}
              >
                {titleText}
              </h2>
            </div>

            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                color: 'var(--color-text-tertiary, #71717a)',
                marginBottom: 'var(--space-1, 4px)',
              }}
            >
              {creatorText}
            </div>
            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                color: 'var(--color-text-tertiary, #71717a)',
                marginBottom: 'var(--space-3, 12px)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1-5, 6px)',
              }}
            >
              <Eye size={14} aria-hidden />
              <span>
                {viewsText} · {agoText}
              </span>
            </div>

            <p
              style={{
                margin: '0 0 var(--space-5, 20px)',
                color: 'var(--color-text-secondary, #52525b)',
                lineHeight: 'var(--line-height-relaxed, 1.55)',
                fontSize: 'var(--font-size-sm, 14px)',
              }}
            >
              {descriptionText}
            </p>

            {/* Spacer keeps the action row pinned to the bottom of
             *  the pane, so multi-line descriptions don't push it. */}
            <div style={{ flex: 1 }} />

            {showActions && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2, 8px)',
                  flexWrap: 'wrap',
                  paddingTop: 'var(--space-4, 16px)',
                  borderTop: '1px solid var(--color-border-subtle, rgba(0,0,0,0.06))',
                }}
              >
                <ActionPill icon={<Heart size={16} />} label={t('hero.spotlight.action.like')} />
                <ActionPill
                  icon={<Bookmark size={16} />}
                  label={t('hero.spotlight.action.later')}
                />
                <ActionPill icon={<List size={16} />} label={t('hero.spotlight.action.list')} />
                <div style={{ flex: 1 }} />
                <ActionPill icon={<Share2 size={16} />} label={t('hero.spotlight.action.share')} />
              </div>
            )}

            {/* Primary CTA — hidden when the action row is rendered
             *  (the play button on the video is the primary CTA in
             *  that case). */}
            {!showActions && ctaText && (
              <Button
                href={ctaHref}
                style={{
                  marginTop: 'var(--space-3, 12px)',
                  alignSelf: 'flex-start',
                }}
              >
                {ctaText}
              </Button>
            )}
          </div>
        </Card>
      </div>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media (max-width: 1024px) {
          .hero-spotlight-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `,
        }}
      />
    </BlockRoot>
  );
}

// =============================================================================
// ActionPill — pill button with icon glyph + label
// =============================================================================
//
// {@code icon} is a ReactNode so callers can pass either an inline SVG
// (preferred — renders identically across OSes) or a unicode glyph.

function ActionPill({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <Button
      variant="outline"
      size="sm"
      radius="pill"
      style={{
        gap: 'var(--space-1-5, 6px)',
        borderColor: 'var(--color-border-default, rgba(0,0,0,0.12))',
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 16,
          height: 16,
        }}
      >
        {icon}
      </span>
      <span>{label}</span>
    </Button>
  );
}

// `Localized` shape is exported here as a courtesy alias for callers
// that want to type CMS-supplied strings consistently with the rest
// of the block library; the block itself uses plain strings via
// pre-resolved props.
export type { Localized };
